import { useParams } from "react-router-dom";
import styles from "./ServiceRequestDetailsPage.module.scss";
import {
	useLazyGetServiceRequestInstanceQuery,
	useAssignServiceRequestMutation,
	useMoveServiceRequestInProgressMutation,
	useCompleteServiceRequestMutation,
} from "../../../../../service/customersupport-service";
import { useEffect, useState, useRef } from "react";
import { getErrorMessage } from "../../../../../utils/error-handler";
import { toast } from "react-toastify";
import RenderSection from "../components/DynamicFormRenderingEngine/RenderSection/RenderSection";
import RenderGeneralDetails from "../components/DynamicFormRenderingEngine/RenderGeneralDetails/RenderGeneralDetails";
import { BounceLoader } from "react-spinners";
import Button from "../../../../../common/components/xepp/button/Button";
import { CaretDownIcon as DropdownIcon } from "../../../../../assets/icons";
import useOutsideClick from "../../../../../hooks/useOutsideClick";
import { ServiceRequestStatusMap, ServiceRequestStatus } from "../../../../../model/service-request/service-request-instance";
import { useDispatch } from "react-redux";
import { hideLoader, showLoader } from "../../../../../stores/slices/loader-slice";
import { useCan } from "../../../../../guards/useCan";
import { UtilityPermissions } from "../../../../../model/permissions/permissions";

const ServiceRequestDetailsPage = () => {
	let { instanceId = "" } = useParams();

	const dispatch = useDispatch();
	const [getServiceRequestInstance, { data, isLoading, isSuccess }] = useLazyGetServiceRequestInstanceQuery();
	const [assignServiceRequest, { isSuccess: isAssignedSuccess }] = useAssignServiceRequestMutation();
	const [moveServiceRequestInProgress, { isSuccess: isMoveIneProgresSuccess }] = useMoveServiceRequestInProgressMutation();
	const [completeServiceRequest, { isSuccess: isCompleteSuccess }] = useCompleteServiceRequestMutation();
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [currentStatus, setCurrentSatatus] = useState<ServiceRequestStatus>();
	const dropdownRef = useRef<HTMLDivElement>(null);
	const { can } = useCan();

	// Map status values to display text and API endpoints
	const statusOptions: Array<{ value: ServiceRequestStatus; displayText: string; endpoint: "assign" | "move-in-progress" | "complete" | null }> = [
		{ value: "Unassigned", displayText: "Unassigned", endpoint: null },
		{ value: "Assigned", displayText: "Assigned", endpoint: "assign" },
		{ value: "InProgress", displayText: "In progress", endpoint: "move-in-progress" },
		{ value: "Completed", displayText: "Completed", endpoint: "complete" },
	];

	useEffect(() => {
		if (data != undefined) {
			setCurrentSatatus(ServiceRequestStatusMap[data!.status]);
		}
	}, [data, isLoading, isSuccess]);

	// Get current status display text
	const getCurrentStatusText = (): string => {
		if (!currentStatus) return "Update Status";
		const option = statusOptions.find(opt => opt.value === currentStatus);
		return option?.displayText || "Update Status";
	};

	useOutsideClick({
		ref: dropdownRef,
		handler: () => {
			setIsDropdownOpen(false);
		},
	});
	const loadInitialData = async () => {
		dispatch(showLoader());
		await getServiceRequestInstance(Number(instanceId))
			.unwrap()
			.catch(err => getErrorMessage(err.data));
		dispatch(hideLoader());
	};

	useEffect(() => {
		loadInitialData();
	}, [getServiceRequestInstance]);

	const handleStatusChange = async (option: (typeof statusOptions)[0]) => {
		if (!data?.instanceId || !option.endpoint) return;

		setIsDropdownOpen(false);
		dispatch(showLoader());
		try {
			const body = { serviceRequestId: data.instanceId };
			let mutation;

			switch (option.endpoint) {
				case "assign":
					mutation = assignServiceRequest(body);
					break;
				case "move-in-progress":
					mutation = moveServiceRequestInProgress(body);
					break;
				case "complete":
					mutation = completeServiceRequest(body);
					break;
				default:
					return;
			}

			await mutation.unwrap();
			// Wait 5 seconds before refetching the data to get updated status

			switch (option.endpoint) {
				case "assign":
					toast.success("Status will be changed to Assigned soon");
					setCurrentSatatus(ServiceRequestStatusMap[2]);
					break;
				case "move-in-progress":
					toast.success("Status will be changed to In Progress soon");
					setCurrentSatatus(ServiceRequestStatusMap[3]);
					break;
				case "complete":
					toast.success("Status will be changed to Completed soon");
					setCurrentSatatus(ServiceRequestStatusMap[4]);
					break;
				default:
					return;
			}
		} catch (err: any) {
			toast.error(getErrorMessage(err?.data) ?? "An error occurred while updating the status");
		} finally {
			dispatch(hideLoader());
		}
	};

	return (
		<>
			<div className={styles.mainContainer}>
				<div className={styles.header}>
					<span>Customer Request Details</span>
					<div ref={dropdownRef} className={styles.dropdownContainer}>
						<Button
							variant="secondary"
							text={getCurrentStatusText()}
							leftIcon={<DropdownIcon className={styles.icon} width={15} height={15} />}
							onClick={() => setIsDropdownOpen(!isDropdownOpen)}
						/>
						{isDropdownOpen && (
							<div className={styles.dropdownMenu}>
								<ul>
									{statusOptions
										.filter(option => option.value !== "Assigned" || can(UtilityPermissions.AssignStep))
										.map(option => {
										const isCurrentStatus = currentStatus === option.value;
										return (
											<li
												key={option.value}
												className={`${styles.dropdownItem} ${isCurrentStatus ? styles.selected : ""} ${!option.endpoint ? styles.disabled : ""}`}
												onClick={() => {
													if (option.endpoint) {
														handleStatusChange(option);
													}
												}}
											>
												<span>{option.displayText}</span>
											</li>
										);
									})}
								</ul>
							</div>
						)}
					</div>
				</div>
				<div className={styles.containersWrapper}>
					<RenderGeneralDetails sectionInstance={data!} status={currentStatus} />
					{data?.sectionInstances.map(si => (
						<RenderSection key={si.id} sectionInstance={si} />
					))}
				</div>
			</div>
		</>
	);
};

export default ServiceRequestDetailsPage;
